import {FC, useState, useContext} from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import {useMutation, useApolloClient} from '@apollo/client';
import {useTranslation} from 'react-i18next';
import {ENABLE_PALOWAN_VACATION_ASSIGNMENT} from 'graphql/palowan-vacation/Mutations';
import {alertContext} from 'contextApi/context/error';
import {PalowanVacationAssignment} from 'models/PalowanVacationAssignment';

interface PalowanVacationEnableModalProps {
  open: boolean;
  assignment: PalowanVacationAssignment | null;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Confirmation modal for enabling a previously disabled palowan-level vacation assignment.
 * Prompts the admin to confirm the action before executing the
 * ENABLE_PALOWAN_VACATION_ASSIGNMENT mutation. On success, dispatches a
 * success Snackbar, refetches the assignments list, and calls onSuccess.
 * On failure, inspects the error message to dispatch a context-specific
 * error Snackbar.
 *
 * @param open - Whether the dialog is visible.
 * @param assignment - The vacation assignment to enable, or null if none is selected.
 * @param onClose - Callback invoked when the dialog should close without action.
 * @param onSuccess - Callback invoked after the assignment is successfully enabled.
 */
const PalowanVacationEnableModal: FC<PalowanVacationEnableModalProps> = ({
  open,
  assignment,
  onClose,
  onSuccess,
}) => {
  const {t} = useTranslation();
  const {dispatch} = useContext(alertContext);
  const client = useApolloClient();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [enableVacationAssignment] = useMutation(
    ENABLE_PALOWAN_VACATION_ASSIGNMENT
  );

  /**
   * Executes the enable mutation and handles success and error outcomes.
   * Inspects the server error message to dispatch the most specific error
   * Snackbar: "already enabled", "not found", or a generic fallback.
   */
  const handleConfirm = async (): Promise<void> => {
    setIsSubmitting(true);
    try {
      await enableVacationAssignment({variables: {id: assignment!.id}});
      dispatch({
        type: 'show',
        payload: {
          message: t('leaves.PalowanVacationEnableModal.successMessage'),
          level: 'success',
          duration: 5000,
        },
      });
      await client.refetchQueries({include: ['getPalowanVacationAssignments']});
      onSuccess();
    } catch (err: unknown) {
      const apolloErr = err as {
        message?: string;
        graphQLErrors?: Array<{message: string}>;
      };
      const errorMessage =
        apolloErr?.graphQLErrors?.[0]?.message ?? apolloErr?.message ?? '';

      let i18nKey: string;
      if (errorMessage.includes('habilitado')) {
        i18nKey = 'leaves.PalowanVacationEnableModal.alreadyEnabledError';
      } else if (errorMessage.includes('encontró')) {
        i18nKey = 'leaves.PalowanVacationEnableModal.notFoundError';
      } else {
        i18nKey = 'leaves.PalowanVacationEnableModal.errorMessage';
      }

      dispatch({
        type: 'show',
        payload: {
          message: t(i18nKey),
          level: 'error',
          duration: 5000,
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('leaves.PalowanVacationEnableModal.title')}</DialogTitle>
      <DialogContent>
        <Typography>
          {t('leaves.PalowanVacationEnableModal.message', {
            palowanName: `${assignment?.palowan.firstName} ${assignment?.palowan.lastName}`,
            from: assignment?.seniorityFromYears,
            to: assignment?.seniorityToYears,
          })}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          {t('leaves.PalowanVacationEnableModal.cancelButton')}
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={!assignment || isSubmitting}
          color="primary"
          variant="contained"
        >
          {t('leaves.PalowanVacationEnableModal.confirmButton')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PalowanVacationEnableModal;
